import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Mail, MapPin, Send } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { contactFormSchema } from '../utils/validation';
import api from '../services/api';

const serviceOptions = [
  { label: 'Basic SEO Fixup - $1,000', value: 'Basic SEO Fixup' },
  { label: 'Complete Web Redesign - $2,000', value: 'Complete Web Redesign' },
  { label: 'Hosting & Management - $20/mo', value: 'Hosting & Management' },
  { label: 'Custom App Development - $5,000+', value: 'Custom App Development' },
];

const ContactPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const location = useLocation();
  const packageInfo = location.state as { selectedPackage?: string; packagePrice?: number; packageId?: number } | null;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(contactFormSchema),
    mode: 'onChange'
  });

  // Pre-fill form with package information
  useEffect(() => {
    if (packageInfo?.selectedPackage) {
      setValue('subject', `Inquiry about ${packageInfo.selectedPackage} Package`);
      setValue('serviceType', packageInfo.selectedPackage);
      setValue('message', `I'm interested in the ${packageInfo.selectedPackage} package${packageInfo.packagePrice ? ` ($${packageInfo.packagePrice})` : ''}. `);
    }
  }, [packageInfo, setValue]);

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await api.post('/contact', {
        name: data.name,
        email: data.email,
        phone: data.phone || '',
        company: data.company || '',
        serviceType: data.serviceType || '',
        subject: data.subject,
        message: data.message,
      });
      setSubmitStatus('success');
      reset();
    } catch (error) {
      // Fallback to mailto if API fails
      const subject = encodeURIComponent(data.subject || `New Inquiry: ${data.serviceType || 'General'}`);
      const body = encodeURIComponent(
        `Name: ${data.name}\nEmail: ${data.email}\nPhone: ${data.phone || 'N/A'}\nCompany: ${data.company || 'N/A'}\nService: ${data.serviceType || 'N/A'}\n\nMessage:\n${data.message}`
      );
      window.location.href = `mailto:nikos.local.web.design@gmail.com?subject=${subject}&body=${body}`;
      setSubmitStatus('success');
      reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - iModernize | Web Design & SEO in NH</title>
        <meta name="description" content="Contact iModernize for web design, SEO, and custom app development services. Based in Merrimack, NH — serving clients nationwide. Get a free consultation today." />
        <meta name="keywords" content="contact web designer, SEO services NH, web design Merrimack NH, custom app development, website redesign quote" />
      </Helmet>

      <div className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Let's Work Together
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to start your project? We'd love to hear from you. 
              Let's discuss how we can help bring your vision to life.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="card p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                Send us a Message
              </h2>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-green-800">
                    Thank you for your message! We'll get back to you within 24 hours.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-800">
                    Sorry, there was an error sending your message. Please try again.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name', { required: 'Name is required' })}
                      className={`input ${errors.name ? 'border-red-500' : ''}`}
                      placeholder="John Doe"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      className={`input ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="john@example.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      {...register('phone')}
                      className="input"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      {...register('company')}
                      className="input"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Needed
                    </label>
                    <select
                      id="serviceType"
                      {...register('serviceType')}
                      className="select"
                    >
                      <option value="">Select a service</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                      <option value="Other">Other (describe in message)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    {...register('subject', { required: 'Subject is required' })}
                    className={`input ${errors.subject ? 'border-red-500' : ''}`}
                    placeholder="Project inquiry"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    rows={6}
                    {...register('message', { required: 'Message is required' })}
                    className={`textarea ${errors.message ? 'border-red-500' : ''}`}
                    placeholder="Tell us about your project..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="card p-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  Get in Touch
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <Mail className="h-6 w-6 text-blue-600 mr-4 mt-1" />
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Email</h3>
                      <a href="mailto:nikos.local.web.design@gmail.com" className="text-blue-600 hover:underline">
                        nikos.local.web.design@gmail.com
                      </a>
                      <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <MapPin className="h-6 w-6 text-blue-600 mr-4 mt-1" />
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Location</h3>
                      <p className="text-gray-600">Merrimack, NH</p>
                      <p className="text-sm text-gray-500">Serving clients nationwide</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  What Happens Next?
                </h3>
                <ol className="space-y-3">
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mr-3 mt-0.5">
                      1
                    </span>
                    <span className="text-gray-600">We'll review your project details</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mr-3 mt-0.5">
                      2
                    </span>
                    <span className="text-gray-600">Schedule a discovery call</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mr-3 mt-0.5">
                      3
                    </span>
                    <span className="text-gray-600">Provide detailed proposal</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mr-3 mt-0.5">
                      4
                    </span>
                    <span className="text-gray-600">Start building your project</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;